import { Accordion, AccordionDetails, AccordionSummary, Card, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

import { Strings } from '@app/constants';
import { DataNotFound, formatDate, Scrollbar, StatusTag } from '@app/components';
import { SelectedUsers } from './selected-users';
import {
  UserCard,
  DashboardDetailsCard,
  PieChartCard,
  ProjectSummaryCard,
} from '@app/components/card';
import { SelectedInterns } from './selected-interns';
import { EmployeeInternGrowth } from './employee-intern-growth';
import { getDashboardData, getTasksOverview } from '../slice';
import { selectDashboardState, selectGetTasksOverviewState } from '../selecter';
import { useRequestState } from '@app/hooks';
import { InfoStatusCard } from '@app/components/card/info-status-card';
import { ILeaveDetails } from '@app/modules/add-leave-employee';
import { IAddProjectSchema } from '@app/modules/projects/types';
import { StyledTitle } from '../style';
import { IUser } from '@app/modules/manager-dashboard';
import { PATH_DASHBOARD } from '@app/routes/paths';
import { CustomAvatar } from '@app/components/custom-avatar';
import { calculateTotalLeaves } from '@app/configs';

const DashboardPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedProject, setSelectedProject] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getDashboardData());
  }, [dispatch]);

  const { data } = useRequestState({
    stateSelector: selectDashboardState,
    errorShown: true,
  });
  const { data: Tasks } = useRequestState({
    stateSelector: selectGetTasksOverviewState,
    errorShown: true,
  });
  const projectsWithUserNames = data?.projects?.projects?.map((project: any) => ({
    ...project,
    managers: project?.managers
      ?.map((managerId: number) => {
        const user = data?.projects?.users?.find((user: IUser) => user.id === managerId);
        return user
          ? `${user.firstName as string} ${user.lastName as string}`
          : `User ${managerId}`;
      })
      .join(', '),
  }));
  const fullName: string =
    (String(data?.userForDashboard?.firstName) ?? '') +
    ' ' +
    (String(data?.userForDashboard?.lastName) ?? '');

  const handleCardClick = (id: number) => {
    setSelectedProject(id);
    dispatch(getTasksOverview(id));
  };

  useEffect(() => {
    if (data?.projects && data.projects.projects.length > 0) {
      const firstProject = data.projects.projects[0];
      handleProjectSelect(firstProject.name);
    }
  }, [data]);

  const handleProjectSelect = (id: number) => {
    setSelectedProject(id);
  };

  useEffect(() => {
    if (data && data?.projects?.projects?.length > 0) {
      const firstProjectId = data.projects.projects[0].id;
      setSelectedProject(firstProjectId);
      dispatch(getTasksOverview(firstProjectId));
    }
  }, [data, dispatch]);

  const [expanded, setExpanded] = useState<string | false>(false);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  return (
    <>
      <Scrollbar>
        <Stack flexDirection={{ sm: 'column', md: 'row' }} mt={2}>
          <Card
            sx={{
              ml: { md: 2, xs: 1 },
              mr: { xs: 1, md: 0 },
              mb: 2,
              width: { md: '53%', sm: '97%' },
              borderRadius: '5px',
              backgroundImage: 'url(/assets/images/dashboard-background.png)',
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundAttachment: 'fixed',
            }}
          >
            <Stack fontSize={18} fontWeight={700} m={'20px 0px 15px 20px'}>
              {Strings.common.welcomeBackCaption(fullName)}
            </Stack>
            <Stack direction={'row'} mb={1}>
              <Stack
                width={'300px'}
                m={'15px 0px 20px 20px'}
                color={theme.palette.grey[600]}
                fontSize={14}
              >
                {Strings.dashboard.caption}
              </Stack>
              <Stack mt={-6} display={{ xs: 'none', md: 'inline' }}>
                <img
                  src={'/assets/images/dashboard-image.png'}
                  style={{
                    objectFit: 'contain',
                    height: '200px',
                    width: '200px',
                    padding: 0,
                  }}
                />
              </Stack>
            </Stack>
          </Card>

          <Stack sx={{ width: { md: '45%', xs: '98%' } }} mr={{ md: 2, xs: 1 }}>
            <Stack direction={{ md: 'row', xs: 'row' }} width={{ xs: '100%' }}>
              <UserCard
                title={Strings.page_title.candidates}
                value={data?.numberOfCandidate}
                iconImg="ic_candidates"
                width={{ md: '100%', xs: '50%' }}
                user
                onClickUserCard={() => {
                  navigate(PATH_DASHBOARD.candidateList);
                }}
              />
              <UserCard
                title={Strings.page_title.employees}
                value={data?.numberOfEmployee}
                iconImg="ic_employees"
                width={{ md: '100%', xs: '50%' }}
                user
                onClickUserCard={() => {
                  navigate(PATH_DASHBOARD.employeeList);
                }}
              />
            </Stack>
            <Stack direction={{ md: 'row', xs: 'row' }}>
              <UserCard
                title={Strings.page_title.interviewees}
                value={data?.interviewee}
                iconImg="ic_interviewers"
                width={{ md: '100%', xs: '50%' }}
                user
                onClickUserCard={() => {
                  navigate(PATH_DASHBOARD.IntervieweeList);
                }}
              />
              <UserCard
                title={Strings.page_title.interns}
                value={data?.numberOfInterns}
                iconImg="ic_interns"
                width={{ md: '100%', xs: '50%' }}
                user
                onClickUserCard={() => {
                  navigate(PATH_DASHBOARD.internList);
                }}
              />
            </Stack>
          </Stack>
        </Stack>
        <Stack flexDirection={{ md: 'row', sm: 'column' }} gap={2}>
          <Stack sx={{ width: { md: '43%', sm: '100%' } }}>
            <Card sx={{ borderRadius: '5px', ml: { md: 2, xs: 1 }, mr: { xs: 1 } }}>
              <Stack fontSize={17} fontWeight={700} p={1.5} ml={2}>
                {Strings.dashboard.attendence}
              </Stack>
              <Stack justifyContent={'space-evenly'} direction={'row'} mb={1.3}>
                <Stack>
                  <Stack color={theme.palette.grey[500]} fontSize={13}>
                    {Strings.dashboard.total}
                  </Stack>
                  <Stack fontSize={20} fontWeight={600}>
                    {data?.total}
                  </Stack>
                </Stack>
                <Stack>
                  <Stack color={theme.palette.grey[500]} fontSize={13}>
                    {Strings.dashboard.onLeave}
                  </Stack>
                  <Stack fontWeight={600} fontSize={20} ml={1.5}>
                    {data?.onLeave}
                  </Stack>
                </Stack>
                <Stack>
                  <Stack color={theme.palette.grey[500]} fontSize={13}>
                    {Strings.dashboard.present}
                  </Stack>
                  <Stack fontWeight={600} fontSize={20} ml={1}>
                    {data?.present}
                  </Stack>
                </Stack>
              </Stack>
            </Card>
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: { xs: 290, md: 165 },
                width: { md: '95%', sm: '80%' },
                borderRadius: '5px',
                overflow: 'hidden',
                ml: { md: 2, xs: 1 },
                mt: 2,
                mb: 1,
                mr: { xs: 1 },
              }}
            >
              <StyledTitle>{Strings.dashboard.approvedLeaves}</StyledTitle>
              <Scrollbar sx={{ maxHeight: { xs: 230, md: 230 } }}>
                {data?.approvedLeaves?.length > 0 ? (
                  data?.approvedLeaves?.map((option: ILeaveDetails) => {
                    const openDetailsPage = () => {
                      navigate(PATH_DASHBOARD.leaveDetail, {
                        state: { id: option?.id, update: option.update },
                      });
                    };
                    const totalLeaveCount: number = calculateTotalLeaves(option.periods);
                    return (
                      <InfoStatusCard
                        key={option.id}
                        titleName={`${option.user.firstName} ${option.user.lastName}`}
                        subTitleHead={
                          option.periods.length > 1
                            ? `${option.from} to`
                            : formatDate(option?.periods[0]?.date)
                        }
                        subTitleValue={
                          option?.periods?.length > 1
                            ? `${option.to} (${totalLeaveCount} days)`
                            : `(${totalLeaveCount} days)`
                        }
                        actionHead={`${Strings.dashboard.approvedBy} :`}
                        actionValue={`${option.approvedUser?.firstName ?? ''} ${
                          option.approvedUser?.lastName ?? ''
                        }`}
                        onClick={openDetailsPage}
                      />
                    );
                  })
                ) : (
                  <DataNotFound height={125} />
                )}
              </Scrollbar>
            </Card>
          </Stack>

          <Stack gap={3} sx={{ width: { md: '54%', sm: '100%' } }}>
            <Card
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: { md: 290 },
                height: { md: 290 },
                width: { md: '100%', sm: '96%' },
                borderRadius: '5px',
                overflow: 'hidden',
                ml: { xs: 1, md: 0 },
                mr: { xs: 1, md: 2 },
              }}
            >
              <StyledTitle>{Strings.dashboard.requestedLeaves}</StyledTitle>
              <Scrollbar sx={{ maxHeight: { xs: 230, md: 240 } }}>
                {data?.requestedLeaves?.leave?.length > 0 ? (
                  data?.requestedLeaves?.leave?.map((option: ILeaveDetails) => {
                    const openDetailsPage = () => {
                      navigate(PATH_DASHBOARD.leaveDetail, { state: { id: option?.id } });
                    };
                    const totalLeaveCount: number = calculateTotalLeaves(option.periods);
                    return (
                      <InfoStatusCard
                        key={option.id}
                        titleName={`${option.user.firstName} ${option.user.lastName}`}
                        subTitleHead={
                          option.periods.length > 1
                            ? `${option.from} to`
                            : formatDate(option?.periods[0]?.date)
                        }
                        subTitleValue={option?.periods?.length > 1 ? option.to : ''}
                        actionTime={
                          option.periods.length === 1
                            ? option?.periods[0]?.period
                            : `${totalLeaveCount} days`
                        }
                        onClick={openDetailsPage}
                      />
                    );
                  })
                ) : (
                  <DataNotFound height={240} />
                )}
              </Scrollbar>
            </Card>
          </Stack>
        </Stack>
        <Stack>
          <EmployeeInternGrowth data={data} CompanyGrowth={data?.companyGrowth} />
        </Stack>
        <Stack>
          <SelectedInterns />
        </Stack>
        <Stack>
          <SelectedUsers />
        </Stack>
        <Stack>
          <ProjectSummaryCard data={data?.projects} />
        </Stack>
        <Stack>
          <Stack flexDirection={{ md: 'row', sm: 'column' }} gap={2}>
            <Stack gap={3} width={{ md: '48%', sm: '100%' }} ml={{ md: 2, xs: 0 }}>
              <Stack sx={{ display: { xs: 'none', md: 'inline' } }}>
                <PieChartCard taskOverview={Tasks} selectedProject={selectedProject} />
              </Stack>
              <DashboardDetailsCard title="Clients" height={350} rows={data?.clients} />
            </Stack>
            <Stack gap={3} sx={{ width: { md: '49%', sm: '100%' }, mr: { md: 1.5 } }}>
              <Stack height={{ md: 330 }} maxHeight={{ xs: 330 }}>
                <Card
                  sx={{
                    maxHeight: { xs: 330 },
                    height: { md: 330 },
                    width: { md: '100%', sm: '96%' },
                    borderRadius: '5px',
                    overflow: 'hidden',
                    ml: { xs: 1, md: 0 },
                    mr: { xs: 1, md: 0 },
                  }}
                >
                  <Stack
                    fontWeight={700}
                    fontSize={17}
                    sx={{
                      paddingLeft: 2,
                      marginTop: 1,
                    }}
                  >
                    {Strings.page_title.projects}
                  </Stack>
                  <Stack sx={{ display: { xs: 'none', md: 'inline' } }}>
                    <Scrollbar
                      sx={{
                        maxHeight: { xs: 290 },
                        height: { md: 290 },
                      }}
                    >
                      {projectsWithUserNames?.length > 0 ? (
                        projectsWithUserNames?.map((option: IAddProjectSchema) => {
                          return (
                            <InfoStatusCard
                              key={option.id}
                              titleName={option.name}
                              subTitleHead={`${Strings.projects.managedBy} :`}
                              subTitleValue={option.managers}
                              status={option.priority}
                              image={option.image}
                              onClick={() => {
                                handleCardClick(option.id);
                              }}
                              isSelected={selectedProject === option.id}
                            />
                          );
                        })
                      ) : (
                        <DataNotFound height={300} />
                      )}
                    </Scrollbar>
                  </Stack>
                  <Stack
                    sx={{
                      maxHeight: { xs: 330 },
                      height: { md: 330 },
                      width: { md: '100%', sm: '96%' },
                      borderRadius: '5px',
                      overflow: 'hidden',
                      ml: { xs: 1, md: 0 },
                      mr: { xs: 1, md: 0 },
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    <Scrollbar sx={{ maxHeight: { xs: 260 } }}>
                      {projectsWithUserNames?.map((option: any) => (
                        <Accordion
                          key={option.id}
                          expanded={expanded === option.id}
                          onChange={handleAccordionChange(option.id)}
                          sx={{ display: { md: 'none', xs: 'inline' } }}
                        >
                          <Card
                            sx={{ borderRadius: '5px', margin: 1 }}
                            onClick={() => {
                              handleCardClick(option.id);
                            }}
                          >
                            <Stack
                              gap={1}
                              justifyContent={'space-between'}
                              direction={'row'}
                              m={0.5}
                            >
                              <Stack direction="row" gap={2}>
                                <CustomAvatar name={option.name} color="info" src={option.image} />
                                <Stack>
                                  <Stack> {option.name}</Stack>
                                  <Stack
                                    flexDirection={'row'}
                                    fontSize={{ md: 10, xs: 7 }}
                                    gap={0.5}
                                  >
                                    <Stack color={theme.palette.grey[600]}>
                                      {Strings.projects.managedBy}
                                    </Stack>
                                    <Stack mr={-1}> {option.managers}</Stack>
                                  </Stack>
                                </Stack>
                              </Stack>
                              <Stack direction={'row'}>
                                <Stack justifyContent={'center'} alignItems={'center'}>
                                  <StatusTag status={option.priority ?? ''} />
                                </Stack>
                                <AccordionSummary
                                  expandIcon={<ArrowDropDownIcon />}
                                  aria-controls="panel2-content"
                                  id="panel2-header"
                                  sx={{ ml: -2, mr: -1 }}
                                ></AccordionSummary>
                              </Stack>
                            </Stack>
                          </Card>

                          <AccordionDetails>
                            <Stack
                              sx={{
                                width: { sm: '90%', md: '50%' },
                                height: 'auto',
                                margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
                                marginTop: { xs: '2%', sm: '2%', md: '0%' },
                                borderRadius: '5px',
                                display: { xs: 'inline', md: 'none' },
                              }}
                            >
                              <Stack ml={-3} mr={-3}>
                                <PieChartCard
                                  taskOverview={Tasks}
                                  selectedProject={selectedProject}
                                />
                              </Stack>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Scrollbar>
                  </Stack>
                </Card>
              </Stack>
              <Stack>
                <DashboardDetailsCard title="Assets" height={350} data={data?.assets} />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Scrollbar>
    </>
  );
};

export { DashboardPage };
